/**
 * CR-8627: the ERP price changes behind one segment of the "Customer price
 * changes" chart.
 *
 * The rows newest first, Export to Excel, a pager. The PO number opens the line
 * in a new browser tab. Unlike the supplier requests (CR-863) an ERP change has
 * no outcome and no reason, so there are no outcome tabs.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  customerPriceChangesAPI,
  type CustomerPriceChangeRow,
  type CustomerPriceChangesPage,
  type PriceChangeRequestsQuery,
} from '../../../lib/api';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { notifyExportResult, type ExportTruncation } from '../../../lib/exportTruncation';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import ExportTruncationBanner from '../../export/ExportTruncationBanner';
import { DrilldownPager } from '../po-lines/DrilldownPager';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const PAGE_SIZE = 25;

interface CustomerPriceChangesModalProps {
  open: boolean;
  onClose: () => void;
  /** e.g. "Mar 26 · Ironvale Precision". */
  title: string;
  /** The segment, without page. */
  query: Omit<PriceChangeRequestsQuery, 'outcome' | 'page' | 'pageSize'>;
}

/**
 * The click-through dialog.
 *
 * @param open - whether the dialog shows
 * @param onClose - called when it closes
 * @param title - period and supplier of the segment
 * @param query - the segment's scope, dates and suppliers
 */
export function CustomerPriceChangesModal({ open, onClose, title, query }: CustomerPriceChangesModalProps) {
  const { formatDate } = useFormatters();
  const [page, setPage] = useState(1);
  const [data, setData] = useState<CustomerPriceChangesPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [truncation, setTruncation] = useState<ExportTruncation | null>(null);

  const { scope, periodStart, periodEnd, supplierIds } = query;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    customerPriceChangesAPI
      .list({ scope, periodStart, periodEnd, supplierIds, page, pageSize: PAGE_SIZE })
      .then((resp) => { if (!cancelled) setData(resp.data); })
      .catch((err: unknown) => {
        console.error('Customer price changes failed to load:', err);
        if (!cancelled) setError('The price changes could not be loaded. Close this and try again.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, scope, periodStart, periodEnd, supplierIds, page]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const resp = await customerPriceChangesAPI.exportXlsx({ scope, periodStart, periodEnd, supplierIds });
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customer_price_changes_${periodStart}_${periodEnd}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      setTruncation(notifyExportResult(resp, 'Excel export ready'));
    } catch (err: unknown) {
      console.error('Customer price changes export failed:', err);
      toast.error('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  }, [scope, periodStart, periodEnd, supplierIds]);

  const rows = data?.results ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      {/* 10 columns: the supplier requests dialog's width, so both read alike. */}
      <DialogContent className="!max-w-[min(1800px,95vw)] w-[min(1800px,95vw)]">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle>Customer price changes: {title}</DialogTitle>
              <DialogDescription>
                {data ? `${total.toLocaleString()} price change${total === 1 ? '' : 's'} from your ERP` : 'Loading…'}
              </DialogDescription>
            </div>
            <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting || total === 0}>
              {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Export to Excel
            </Button>
          </div>
          <ExportTruncationBanner truncation={truncation} onDismiss={() => setTruncation(null)} />
        </DialogHeader>

        {error && <div className="text-sm text-red-600 py-3">{error}</div>}

        {loading && rows.length === 0 ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 && !error ? (
          <div className="text-sm text-muted-foreground py-10 text-center">No price changes in this segment.</div>
        ) : (
          <ChangesTable rows={rows} formatDate={formatDate} />
        )}

        {total > PAGE_SIZE && (
          <DrilldownPager page={page} totalPages={totalPages} loading={loading} onPageChange={setPage} idPrefix="cpc" />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface ChangesTableProps {
  rows: CustomerPriceChangeRow[];
  formatDate: (value: string) => string;
}

function ChangesTable({ rows, formatDate }: ChangesTableProps) {
  const money = (value: string, currency: string) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return value;
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(n);
    } catch {
      return `${n.toFixed(2)} ${currency}`;
    }
  };
  return (
    <div className="overflow-x-auto max-h-[60vh]">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-background">
          <tr className="border-b text-left text-muted-foreground">
            <th className="py-2 px-2 font-medium whitespace-nowrap">Changed on</th>
            <th className="py-2 px-2 font-medium">Supplier</th>
            <th className="py-2 px-2 font-medium">Site</th>
            <th className="py-2 px-2 font-medium">PO</th>
            <th className="py-2 px-2 font-medium text-right">Line</th>
            <th className="py-2 px-2 font-medium">Part</th>
            <th className="py-2 px-2 font-medium">Description</th>
            <th className="py-2 px-2 font-medium text-right whitespace-nowrap">Price was</th>
            <th className="py-2 px-2 font-medium text-right whitespace-nowrap">New price</th>
            <th className="py-2 px-2 font-medium text-right">Change</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b last:border-0">
              <td className="py-2 px-2 whitespace-nowrap">{formatDate(r.changed_on)}</td>
              <td className="py-2 px-2 min-w-[8rem] max-w-[14rem]">{r.supplier_name}</td>
              <td className="py-2 px-2 min-w-[8rem] max-w-[14rem]">{r.site_name}</td>
              <td className="py-2 px-2 whitespace-nowrap">
                {r.po_line_id ? (
                  <a className="text-primary font-medium hover:underline" href={`/purchasing?tab=po_lines&line=${r.po_line_id}`}
                    target="_blank" rel="noopener noreferrer">{r.po_number}</a>
                ) : r.po_number}
              </td>
              <td className="py-2 px-2 text-right tabular-nums">{r.line_number ?? ''}</td>
              <td className="py-2 px-2 whitespace-nowrap">{r.part_number}</td>
              <td className="py-2 px-2 min-w-[10rem]">{r.description}</td>
              <td className="py-2 px-2 text-right tabular-nums whitespace-nowrap">{money(r.price_was, r.currency)}</td>
              <td className="py-2 px-2 text-right tabular-nums whitespace-nowrap">{money(r.price_new, r.currency)}</td>
              <td className={`py-2 px-2 text-right tabular-nums whitespace-nowrap ${
                r.change_pct === null ? '' : r.change_pct >= 0 ? 'text-red-700' : 'text-emerald-700'
              }`}>
                {r.change_pct === null ? '' : `${r.change_pct >= 0 ? '+' : ''}${r.change_pct.toFixed(1)}%`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
